import React, { useState } from "react";
import styled from "styled-components";
import { css } from "styled-components/macro"; //eslint-disable-line
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, ArrowRight, ArrowLeft, CheckCircle } from "lucide-react";
import AppLogo from "components/common/AppLogo";
import { AppButton } from "components/misc/Buttons";
import { ROUTES } from "constants/routes";

// ─── Design tokens ────────────────────────────────────────────────────────────
const C_PRIMARY = "#ff6c00";
const C_SECONDARY = "#111111";

// ─── Animation ───────────────────────────────────────────────────────────────
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const MDiv = motion.div as any;

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: "easeOut", delay: i * 0.1 },
  }),
};

// ─── Styled ──────────────────────────────────────────────────────────────────

const Page = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fafafa;
  padding: 3rem 1.5rem;
`;

const Card = styled.div`
  width: 100%;
  max-width: 28rem;
  background: white;
  border: 1px solid #e5e7eb;
  padding: 3rem 2.5rem;

  @media (max-width: 480px) {
    padding: 2rem 1.5rem;
  }
`;

const LogoWrap = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 2.5rem;
`;

const Eyebrow = styled.p`
  font-size: 0.65rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.18em;
  color: ${C_PRIMARY};
  margin-bottom: 0.6rem;
`;

const Heading = styled.h1`
  font-size: 1.65rem;
  font-weight: 900;
  color: ${C_SECONDARY};
  margin-bottom: 0.5rem;
`;

const Sub = styled.p`
  font-size: 0.875rem;
  color: #9ca3af;
  margin-bottom: 2rem;
  padding-bottom: 1.75rem;
  border-bottom: 1px solid #f0f0f0;
  line-height: 1.6;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1.25rem;
`;

const Label = styled.label`
  font-size: 0.7rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #374151;
`;

const InputWrapper = styled.div`
  position: relative;
`;

const InputIcon = styled.div`
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  pointer-events: none;
  display: flex;
`;

const Input = styled.input<{ $hasError?: boolean }>`
  width: 100%;
  padding: 0.9rem 1rem 0.9rem 2.75rem;
  border: 1.5px solid ${(p) => (p.$hasError ? "#ef4444" : "#e5e7eb")};
  background: white;
  font-size: 0.9rem;
  color: ${C_SECONDARY};
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.2s ease;

  &:focus {
    border-color: ${(p) => (p.$hasError ? "#ef4444" : C_PRIMARY)};
  }

  &::placeholder {
    color: #c1c1c1;
  }
`;

const ErrorMsg = styled.p`
  font-size: 0.72rem;
  font-weight: 700;
  color: #ef4444;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.78rem;
  font-weight: 700;
  color: #9ca3af;
  text-decoration: none;
  margin-top: 1.5rem;

  &:hover {
    color: ${C_PRIMARY};
  }
`;

// ── Success state ──

const SuccessWrap = styled.div`
  text-align: center;
`;

const SuccessIcon = styled.div`
  width: 4rem;
  height: 4rem;
  background: #fff4ec;
  border-left: 3px solid ${C_PRIMARY};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${C_PRIMARY};
  margin: 0 auto 1.5rem;
`;

const SuccessTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 900;
  color: ${C_SECONDARY};
  margin-bottom: 0.75rem;
`;

const SuccessDesc = styled.p`
  font-size: 0.875rem;
  color: #6b7280;
  line-height: 1.75;
  margin-bottom: 2rem;

  strong {
    color: ${C_SECONDARY};
    font-weight: 900;
  }
`;

const SuccessNote = styled.p`
  font-size: 0.78rem;
  color: #9ca3af;
  line-height: 1.6;
`;

// ─── Component ────────────────────────────────────────────────────────────────

const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState("");
  const [emailErr, setEmailErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [globalErr, setGlobalErr] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setGlobalErr("");

    if (!email) {
      setEmailErr("L'adresse e-mail est requise.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailErr("Format d'e-mail invalide.");
      return;
    }

    setLoading(true);
    try {
      // TODO: appel API mot de passe oublié
      await new Promise((r) => setTimeout(r, 1500));
      setSent(true);
    } catch {
      setGlobalErr("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Page>
      <Card>
        <MDiv variants={fadeUp} initial="hidden" animate="visible" custom={0}>
          <LogoWrap>
            <AppLogo variant="light" size="md" href="/" />
          </LogoWrap>
        </MDiv>

        {sent ? (
          /* ── État succès ── */
          <MDiv variants={fadeUp} initial="hidden" animate="visible" custom={0}>
            <SuccessWrap>
              <SuccessIcon>
                <CheckCircle size={24} />
              </SuccessIcon>
              <SuccessTitle>E-mail envoyé !</SuccessTitle>
              <SuccessDesc>
                Un lien de réinitialisation a été envoyé à{" "}
                <strong>{email}</strong>. Vérifiez votre boîte de réception (et
                vos spams).
              </SuccessDesc>
              <AppButton
                variant="primary"
                size="md"
                fullWidth
                to={ROUTES.LOGIN}
                iconLeft={<ArrowLeft size={15} />}
              >
                Retour à la connexion
              </AppButton>
              <SuccessNote style={{ marginTop: "1.25rem" }}>
                Vous n'avez pas reçu l'e-mail ?{" "}
                <button
                  type="button"
                  onClick={() => setSent(false)}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: C_PRIMARY,
                    fontWeight: 700,
                    fontSize: "inherit",
                    padding: 0,
                  }}
                >
                  Renvoyer
                </button>
              </SuccessNote>
            </SuccessWrap>
          </MDiv>
        ) : (
          /* ── Formulaire ── */
          <>
            <MDiv
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              custom={1}
            >
              <Eyebrow>Sécurité</Eyebrow>
              <Heading>Mot de passe oublié ?</Heading>
              <Sub>
                Saisissez l'adresse e-mail associée à votre compte. Nous vous
                enverrons un lien pour réinitialiser votre mot de passe.
              </Sub>
            </MDiv>

            <MDiv
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              custom={2}
            >
              <form onSubmit={handleSubmit} noValidate>
                {globalErr && (
                  <div
                    style={{
                      padding: "0.875rem 1rem",
                      background: "#fef2f2",
                      borderLeft: `3px solid #ef4444`,
                      marginBottom: "1.25rem",
                      fontSize: "0.82rem",
                      fontWeight: 600,
                      color: "#b91c1c",
                    }}
                  >
                    {globalErr}
                  </div>
                )}

                <FieldGroup>
                  <Label htmlFor="forgot-email">Adresse e-mail</Label>
                  <InputWrapper>
                    <InputIcon>
                      <Mail size={15} />
                    </InputIcon>
                    <Input
                      id="forgot-email"
                      type="email"
                      placeholder="jean.dupont@exemple.com"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (emailErr) setEmailErr("");
                      }}
                      $hasError={!!emailErr}
                      autoComplete="email"
                      autoFocus
                    />
                  </InputWrapper>
                  {emailErr && <ErrorMsg>{emailErr}</ErrorMsg>}
                </FieldGroup>

                <AppButton
                  type="submit"
                  size="lg"
                  fullWidth
                  loading={loading}
                  iconRight={<ArrowRight size={16} />}
                  style={{ marginTop: "0.5rem" }}
                >
                  Envoyer le lien
                </AppButton>

                <div style={{ textAlign: "center" }}>
                  <BackLink to={ROUTES.LOGIN}>
                    <ArrowLeft size={14} />
                    Retour à la connexion
                  </BackLink>
                </div>
              </form>
            </MDiv>
          </>
        )}
      </Card>
    </Page>
  );
};

export default ForgotPasswordPage;
